import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { of } from 'rxjs';
import * as ProductActions from '../actions/products.action';
import { switchMap, map, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { ProductsService } from '../../services';
import { Store } from '@ngrx/store';
import { getProductsCollectionHandle, getProductsFilteredPageInfo, getProductsPageInfo } from '..';
import { Go } from 'src/app/store';
import { getActiveLanguageNoCulture } from '@teamfoster/sdk/language-ngrx';

@Injectable()
export class ProductsEffects {
  constructor(private actions$: Actions, private service: ProductsService, private store: Store) {}

  LoadProducts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadProducts),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{}, lang]) =>
        this.service.getProducts(undefined, undefined, undefined, lang).pipe(
          map(Products => ProductActions.LoadProductsSuccess({ Products })),
          catchError(error => of(ProductActions.LoadProductsFail({ error })))
        )
      )
    );
  });

  LoadProductsByQuery$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadProductsByQuery),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ searchQuery, identifier, cardType }, lang]) =>
        this.service.getProductsByQuery(searchQuery, cardType ? cardType : identifier === 'global-search' ? 'list' : 'card', lang).pipe(
          map(products => ProductActions.LoadProductsByQuerySuccess({ products, identifier })),
          catchError(error => of(ProductActions.LoadProductsByQueryFail({ error })))
        )
      )
    );
  });

  LoadFilteredProductsByQuery$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadFilterdProductsByQuery),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ searchQuery, after }, lang]) => {
        return this.service.getProducts(after, 25, searchQuery, lang).pipe(
          map(Products => ProductActions.LoadProductsFilteredSuccess({ Products, searchQuery, loadMore: !!after })),
          catchError(error => of(ProductActions.LoadProductsFilteredFail({ error })))
        );
      })
    );
  });

  LoadProductsOfCollection$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadProductsForCollection),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ handle }, lang]) =>
        this.service.getProductsOfCollection(handle, undefined, undefined, lang).pipe(
          map(Products => ProductActions.LoadProductsForCollectionSuccess({ Products })),
          catchError(error => of(ProductActions.LoadProductsFail({ error })))
        )
      )
    );
  });
  LoadMoreProductsOfCollection$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadMoreProductsForCollection),
      withLatestFrom(
        this.store.select(getProductsCollectionHandle),
        this.store.select(getProductsPageInfo),
        this.store.select(getActiveLanguageNoCulture)
      ),

      switchMap(([{}, handle, pageInfo, lang]) =>
        this.service.getProductsOfCollection(handle || '', pageInfo?.cursor, undefined, lang).pipe(
          map(Products => ProductActions.LoadProductsForCollectionSuccess({ Products, loadMore: !!pageInfo?.cursor })),
          catchError(error => of(ProductActions.LoadProductsFail({ error })))
        )
      )
    );
  });

  LoadMoreProduct$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadMoreProducts),
      withLatestFrom(this.store.select(getProductsPageInfo), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{}, pageInfo, lang]) =>
        this.service.getProducts(pageInfo?.cursor, undefined, undefined, lang).pipe(
          map(Products => ProductActions.LoadProductsSuccess({ Products })),
          catchError(error => of(ProductActions.LoadProductsFail({ error })))
        )
      )
    );
  });

  LoadProductByHandle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadProductByHandle),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ handle, loadDetails }, lang]) =>
        this.service.getProductByHandle(handle, lang).pipe(
          map(product => {
            if (!product) {
              return ProductActions.LoadProductByHandleFail({ error: '404' });
            }
            return ProductActions.LoadProductByHandleSuccess({ product, loadDetails, handle });
          }),
          catchError(error => of(ProductActions.LoadProductByHandleFail({ error })))
        )
      )
    );
  });

  LoadProductsFiltered$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadProductsFiltered),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ handle, filters }, lang]) =>
        this.service.getFilteredProducts(handle, filters, '', lang).pipe(
          map(Products => ProductActions.LoadProductsFilteredSuccess({ Products, handle })),
          catchError(error => of(ProductActions.LoadProductsFilteredFail({ error })))
        )
      )
    );
  });

  LoadMoreProductsFiltered$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadMoreProductsFiltered),
      withLatestFrom(
        this.store.select(getProductsCollectionHandle),
        this.store.select(getProductsFilteredPageInfo),
        this.store.select(getActiveLanguageNoCulture)
      ),
      switchMap(([{ filters }, handle, pageInfo, lang]) =>
        this.service.getFilteredProducts(handle || '', filters, pageInfo?.cursor, lang).pipe(
          map(Products => ProductActions.LoadProductsFilteredSuccess({ Products, handle, loadMore: true })),
          catchError(error => of(ProductActions.LoadProductsFail({ error })))
        )
      )
    );
  });

  LoadProductRecommendations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadProductRecommendations),
      withLatestFrom(this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ productId }, lang]) =>
        this.service.getRelatedProducts(productId, lang).pipe(
          map(Products => ProductActions.LoadProductRecommendationsSuccess({ Products, productId })),
          catchError(error => of(ProductActions.LoadProductRecommendationsFail({ error })))
        )
      )
    );
  });

  LoadProductDetailFail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProductActions.LoadProductByHandleFail),
      map(({ error }) => {
        return Go({ path: ['/404'], extras: { skipLocationChange: true } });
      })
    );
  });
}
